import { ChevronRight, ChevronDown, X } from 'lucide-react'
import type { Folder } from '../../lib/api'
import { FolderCard } from './FolderCard'
import { FolderCreateCard } from './FolderCreateCard'
import { FOLDER_COLORS } from './folderColors'

interface FolderSectionProps {
  folders: Folder[]
  activeFolderId?: number | null
  collapsed: boolean
  onToggleCollapse: () => void
  onFolderClick: (id: number) => void
  onClearFolder?: () => void
  onCreateFolder: (name: string) => void
  onRenameFolder: (id: number, name: string) => void
  onDeleteFolder: (id: number) => void
  onChangeFolderColor: (id: number, color: string) => void
  onAddAppToFolder: (folderId: number, appName: string) => void
  getFolderAppNames: (folderId: number) => string[]
  availableApps: { name: string; display_name?: string | null }[]
}

export function FolderSection({
  folders,
  activeFolderId,
  collapsed,
  onToggleCollapse,
  onFolderClick,
  onClearFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onChangeFolderColor,
  onAddAppToFolder,
  getFolderAppNames,
  availableApps,
}: FolderSectionProps) {
  if (folders.length === 0) return null

  const ChevronIcon = collapsed ? ChevronRight : ChevronDown
  const activeFolder = activeFolderId != null ? folders.find(f => f.id === activeFolderId) : null
  const activeFolderColor = activeFolder ? (FOLDER_COLORS[activeFolder.color] || FOLDER_COLORS.gray) : null

  return (
    <div className="mb-4">
      {folders.length > 0 && (
        <div className="flex items-center gap-1.5 mb-2">
          <button
            type="button"
            className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            onClick={onToggleCollapse}
          >
            <ChevronIcon className="w-3.5 h-3.5" />
            Folders
          </button>
          {activeFolder && activeFolderColor && (
            <>
              <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${activeFolderColor.bg} ${activeFolderColor.text}`}
              >
                <span className={`w-2 h-2 rounded-full ${activeFolderColor.dot}`} />
                {activeFolder.name}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onClearFolder?.() }}
                  className="ml-0.5 hover:opacity-70 transition-opacity"
                  title="Clear folder filter"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            </>
          )}
        </div>
      )}

      {!collapsed && (
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {folders.map((folder) => (
            <FolderCard
              key={folder.id}
              folder={folder}
              isActive={activeFolderId === folder.id}
              onClick={() => onFolderClick(folder.id)}
              onRename={(name) => onRenameFolder(folder.id, name)}
              onDelete={() => onDeleteFolder(folder.id)}
              onChangeColor={(color) => onChangeFolderColor(folder.id, color)}
              onAddApps={() => {}}
              folderAppNames={getFolderAppNames(folder.id)}
              availableApps={availableApps}
              onAddAppToFolder={(appName) => onAddAppToFolder(folder.id, appName)}
            />
          ))}
          <FolderCreateCard onCreate={onCreateFolder} />
        </div>
      )}
    </div>
  )
}
